import { useSelector } from 'react-redux';
import { useState, useEffect } from 'react';
import formatCruiseSearchParams from '../../../helpers/formatters/formatCruiseSearchParams';
import formatStateroomsForRequest from '../../../helpers/formatters/formatStateroomsForRequest';
import { DiscountEligibility } from '../../../constants';
import { useNLPConstants, TEMP_NUMBER } from '../constants/nlpSettings';
import getCruiseRateRefundability from '../../../helpers/getCruiseRateRefundability';
import { generateCruiseIntroMessage } from '../introMessages/cruiseIntroMessage';
import getRequest from '../helpers/networkUtil';

const queryString = require('query-string');

/**
 * Utility function to generate an array of URLs for fetching cruise rates data.
 */
const generateRatesUrlArray = (staterooms, cruiseSearchRQ, city, itineraryId) => {
  const ratesUrlArray = [];
  // Create URL for each stateroom
  staterooms.forEach((stateroom) => {
    // Get all the parameters needed for the request
    const {
      adults,
      seniors,
      children,
      lapInfants,
      seatInfants,
      pastPassengerNumber,
      passengerOccupation,
      passengerFirstName,
      passengerMiddleName,
      passengerLastName,
      shipId,
    } = {
      ...formatStateroomsForRequest(stateroom, cruiseSearchRQ.cruiseLineId),
      ...cruiseSearchRQ,
    };
    // Generate the url and add to array
    const url = `cruises/getRates?${queryString.stringify(
      {
        itineraryId,
        adults,
        seniors,
        children,
        lapInfants,
        seatInfants,
        passengerCityCode: city ? city.value : null,
        pastPassengerNumber,
        passengerFirstName,
        passengerMiddleName,
        passengerLastName,
        passengerOccupation,
        shipId,
      },
      { skipNull: true },
    )}`;
    ratesUrlArray.push(url);
  });
  return ratesUrlArray;
};

/**
 * Utility function generates an URL for fetching cruise rate rules for one rateOptionCode
 */
const generateRateRuleUrl = (itineraryId, cruiseSearchRQ, stateroom, city, rateOptionCode) => {
  const passengerOccupation = (stateroom.discounts || []).includes(DiscountEligibility.OCCUPATION)
    ? stateroom.passengerOccupation
    : null;
  const request = {
    itineraryId,
    ...formatStateroomsForRequest(stateroom, cruiseSearchRQ.cruiseLineId),
    rateOptionCode,
    passengerOccupation,
    passengerCityCode: city ? city.value : null,
  };
  const url = `/cruises/getRateOptionRules?${queryString.stringify(request, { skipNull: true })}`;
  return url;
};

/**
 * Utility function collecte all requests to send them in parallel later.
 * For the unique type of "stateroom + rateOptionCode" combination, we collect them
 * only once, because for the same type of guests, the rate rule should be the same.
 */
const collectRateRuleRequests = (
  CRUISE_EXTRACTOR_TIMEOUT_LIMIT,
  ratesArray,
  itineraryId,
  cruiseSearchRQ,
  staterooms,
  city,
  rateRuleRequests,
  rateRuleMap,
) => {
  // Helper function for readability
  const collectForOneRateOption = (rateOption, i) => {
    // Fetch rate rules for this rate option
    const { rateOptionCode } = rateOption;
    const rateRuleUrl = generateRateRuleUrl(
      itineraryId,
      cruiseSearchRQ,
      staterooms[i],
      city,
      rateOptionCode,
    );
    const key = `${JSON.stringify(staterooms[i])} - ${rateOptionCode}`;
    if (!rateRuleMap[key]) {
      rateRuleRequests.push(
        getRequest(rateRuleUrl, 'application/json', CRUISE_EXTRACTOR_TIMEOUT_LIMIT).then(
          (rules) => {
            // Add refundable status and rule text to the map
            let refundableStatusFromRules = null;
            if (rules.rateOptionRules) {
              refundableStatusFromRules = getCruiseRateRefundability(rules.rateOptionRules);
            }
            // oxlint-disable-next-line no-param-reassign
            rateRuleMap[key] = {
              refundableStatusFromRules: refundableStatusFromRules || 'noInfo',
              rules:
                rules &&
                rules.rateOptionRules &&
                rules.rateOptionRules[0] &&
                rules.rateOptionRules[0].text
                  ? rules.rateOptionRules[0].text
                  : null,
            };
          },
        ),
      );
      // oxlint-disable-next-line no-param-reassign
      rateRuleMap[key] = true;
    }
  };
  // Collecting requests for all unique "stateroom + rateOptionCode" combination
  if (!ratesArray) return;
  for (let i = 0; i < ratesArray.length; i += 1) {
    const { cabinCategories } = ratesArray[i];
    if (cabinCategories) {
      cabinCategories.forEach((cabinCategory) => {
        const { rateOptions } = cabinCategory;
        if (rateOptions) {
          rateOptions.forEach((rateOption) => {
            collectForOneRateOption(rateOption, i);
          });
        }
      });
    }
  }
};

/**
 * Utility function for adding rate rules to rates array
 */
const addRateRulesToRatesArray = (ratesArray, rateRuleMap, staterooms) => {
  // Get rules for all rate option
  if (!ratesArray) return;
  ratesArray.forEach((stateroom, i) => {
    const { cabinCategories } = stateroom;
    if (cabinCategories) {
      cabinCategories.forEach((cabinCategory) => {
        const { rateOptions } = cabinCategory;
        if (rateOptions) {
          rateOptions.forEach((rateOption) => {
            // Assign rule to each rate option
            const key = `${JSON.stringify(staterooms[i])} - ${rateOption.rateOptionCode}`;
            // oxlint-disable-next-line no-param-reassign
            rateOption.refundableStatusFromRules = rateRuleMap[key].refundableStatusFromRules;
            // oxlint-disable-next-line no-param-reassign
            rateOption.rules = rateRuleMap[key].rules;
          });
        }
      });
    }
  });
};

/* Self defined hook for extract data and construct system instruction for one cruise
 * based on the input data provided for that cruise */
const useCruiseExtractor = (rawData) => {
  const [extractTriggered, setExtractTriggered] = useState(false);
  const [introMessage, setIntroMessage] = useState('');
  const [cruiseDetails, setCruiseDetails] = useState(undefined);
  const [cruiseRatesArray, setCruiseRatesArray] = useState([]); // Array of rates for each stateroom
  const [error, setError] = useState(null);

  // Get NLP constants
  const nlpConstants = useNLPConstants();
  const CRUISE_EXTRACTOR_TIMEOUT_LIMIT = nlpConstants
    ? parseInt(nlpConstants.CRUISE_EXTRACTOR_TIMEOUT_LIMIT.value, 10)
    : TEMP_NUMBER;

  // Read the itinerary from the raw data for getting cruise details
  const {
    travelOption,
    selectedItineraryRow,
    selectedItineraryRow: { itineraryId },
  } = rawData;

  // Get data for generating URL for rates requests
  const staterooms = useSelector((state) => state.tripSearch.searchObj.staterooms);
  const { city } = useSelector((store) => store.tripSearch.searchObj.cruise.filters);
  const cruiseSearchRQ = formatCruiseSearchParams(selectedItineraryRow, travelOption);

  // Request cruiseDetails and cruiseRates
  useEffect(() => {
    const dispatchGetCruiseDetails = async () => {
      try {
        // Generate the URL for getting cruise details and rates
        const ratesUrlArray = generateRatesUrlArray(staterooms, cruiseSearchRQ, city, itineraryId);
        const detailsUrl = `/cruises/getItineraryDetails?itineraryId=${itineraryId}`;

        /* Get rates promises for all staterooms, but not sending it yet
         * And avoid creating duplicated promises for requests) */
        const ratesPromiseCache = {};
        const ratesPromises = Promise.all(
          ratesUrlArray.map((url) => {
            if (!ratesPromiseCache[url]) {
              ratesPromiseCache[url] = getRequest(
                url,
                'application/json',
                CRUISE_EXTRACTOR_TIMEOUT_LIMIT,
              );
            }
            return ratesPromiseCache[url];
          }),
        );

        // Get cruise details promise, but not sending it yet
        const detailsPromise = getRequest(
          detailsUrl,
          'application/json',
          CRUISE_EXTRACTOR_TIMEOUT_LIMIT,
        );

        // Send ratesPromises and detailsPromise in parallel
        const [ratesArray, details] = await Promise.all([ratesPromises, detailsPromise]);

        // Get rules for all rate options
        const rateRuleRequests = []; // Collect requests for sending in parallel
        const rateRuleMap = {}; // Map stores rate rules for unique stateroom and rateOption pair
        collectRateRuleRequests(
          CRUISE_EXTRACTOR_TIMEOUT_LIMIT,
          ratesArray,
          itineraryId,
          cruiseSearchRQ,
          staterooms,
          city,
          rateRuleRequests,
          rateRuleMap,
        );
        await Promise.all(rateRuleRequests);
        addRateRulesToRatesArray(ratesArray, rateRuleMap, staterooms);

        setCruiseRatesArray(ratesArray);
        setCruiseDetails(details);
      } catch (exceptionError) {
        // Set the error state, so it can be accessed in the interface
        console.log(exceptionError);
        setError(exceptionError);
      }
    };

    if (extractTriggered) {
      setExtractTriggered(false);
      dispatchGetCruiseDetails();
    }
  }, [
    itineraryId,
    extractTriggered,
    cruiseDetails,
    city,
    cruiseSearchRQ,
    staterooms,
    CRUISE_EXTRACTOR_TIMEOUT_LIMIT,
  ]);

  // After getting cruise details, generate the intro message
  useEffect(() => {
    if (cruiseDetails && cruiseRatesArray.length === staterooms.length) {
      const message = generateCruiseIntroMessage(cruiseDetails, cruiseRatesArray);
      setIntroMessage(message);
    }
  }, [cruiseDetails, cruiseRatesArray, staterooms.length, nlpConstants]);

  /* Return both message holder "introMessage", and a trigger. The trigger is for the
   * calling component to start the useCruiseExtractor's internal logic. So, in the calling
   * component, we can choose to extract data at a specific time, like input box clicking */
  return {
    introMessage,
    triggerExtract: setExtractTriggered,
    extractorError: error, // Return error state so it can be accessed in the interface
    setExtractorError: setError,
  };
};

export default useCruiseExtractor;
